/**
 * PumpPortal WebSocket Client for Frontend
 *
 * Real-time token feed from pump.fun via PumpPortal
 */

export interface PumpTokenCreate {
  signature: string;
  mint: string;
  traderPublicKey: string;
  txType: 'create';
  initialBuy: number;
  bondingCurveKey: string;
  vTokensInBondingCurve: number;
  vSolInBondingCurve: number;
  marketCapSol: number;
  name: string;
  symbol: string;
  uri: string;
}

export interface PumpTrade {
  signature: string;
  mint: string;
  traderPublicKey: string;
  txType: 'buy' | 'sell';
  tokenAmount: number;
  solAmount: number;
  bondingCurveKey: string;
  vTokensInBondingCurve: number;
  vSolInBondingCurve: number;
  marketCapSol: number;
  newTokenBalance: number;
}

export interface PumpMigration {
  signature: string;
  mint: string;
  txType: 'migrate';
}

export type PumpEvent = PumpTokenCreate | PumpTrade | PumpMigration;

type EventCallback = (event: PumpEvent) => void;

const PUMPPORTAL_URL = 'wss://pumpportal.fun/api/data';

class PumpPortalWebSocket {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private isConnecting = false;
  private handlers: Map<string, Set<EventCallback>> = new Map();
  private pendingSubscriptions: Array<{ method: string; keys?: string[] }> = [];

  connect(): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }

    if (this.isConnecting) {
      return new Promise((resolve) => {
        const check = setInterval(() => {
          if (this.ws?.readyState === WebSocket.OPEN) {
            clearInterval(check);
            resolve();
          }
        }, 100);
      });
    }

    this.isConnecting = true;

    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(PUMPPORTAL_URL);

        this.ws.onopen = () => {
          console.log('[PumpPortal] Connected');
          this.isConnecting = false;
          this.reconnectAttempts = 0;

          // Send pending subscriptions
          for (const sub of this.pendingSubscriptions) {
            this.send(sub);
          }

          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data) as PumpEvent;
            this.handleEvent(data);
          } catch (e) {
            console.error('[PumpPortal] Parse error:', e);
          }
        };

        this.ws.onerror = (error) => {
          console.error('[PumpPortal] Error:', error);
          this.isConnecting = false;
        };

        this.ws.onclose = () => {
          console.log('[PumpPortal] Disconnected');
          this.isConnecting = false;
          this.attemptReconnect();
        };
      } catch (error) {
        this.isConnecting = false;
        reject(error);
      }
    });
  }

  disconnect(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.handlers.clear();
    this.pendingSubscriptions = [];
  }

  subscribeNewTokens(callback: EventCallback): () => void {
    this.addHandler('newToken', callback);
    const sub = { method: 'subscribeNewToken' };
    this.pendingSubscriptions.push(sub);

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.send(sub);
    }

    return () => {
      this.removeHandler('newToken', callback);
      this.removePendingSubscription('subscribeNewToken');
    };
  }

  subscribeTokenTrades(mints: string[], callback: EventCallback): () => void {
    this.addHandler('tokenTrade', callback);
    const sub = { method: 'subscribeTokenTrade', keys: mints };
    this.pendingSubscriptions.push(sub);

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.send(sub);
    }

    return () => {
      this.removeHandler('tokenTrade', callback);
      this.removePendingSubscription('subscribeTokenTrade', mints);
    };
  }

  subscribeAccountTrades(wallets: string[], callback: EventCallback): () => void {
    this.addHandler('accountTrade', callback);
    const sub = { method: 'subscribeAccountTrade', keys: wallets };
    this.pendingSubscriptions.push(sub);

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.send(sub);
    }

    return () => {
      this.removeHandler('accountTrade', callback);
      this.removePendingSubscription('subscribeAccountTrade', wallets);
    };
  }

  private send(data: unknown): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }

  private addHandler(type: string, callback: EventCallback): void {
    if (!this.handlers.has(type)) {
      this.handlers.set(type, new Set());
    }
    this.handlers.get(type)!.add(callback);
  }

  private removeHandler(type: string, callback: EventCallback): void {
    this.handlers.get(type)?.delete(callback);
  }

  private removePendingSubscription(method: string, keys?: string[]): void {
    const index = this.pendingSubscriptions.findIndex(sub => {
      if (sub.method !== method) return false;
      if (!keys) return true;
      // Check if keys match (for token/account subscriptions)
      return sub.keys && JSON.stringify(sub.keys) === JSON.stringify(keys);
    });
    if (index !== -1) {
      this.pendingSubscriptions.splice(index, 1);
    }
  }

  private handleEvent(event: PumpEvent): void {
    if (event.txType === 'create') {
      this.notify('newToken', event);
    } else if (event.txType === 'buy' || event.txType === 'sell') {
      this.notify('tokenTrade', event);
      this.notify('accountTrade', event);
    } else if (event.txType === 'migrate') {
      this.notify('migrate', event);
    }
  }

  private notify(type: string, event: PumpEvent): void {
    const handlers = this.handlers.get(type);
    if (handlers) {
      for (const handler of handlers) {
        try {
          handler(event);
        } catch (e) {
          console.error('[PumpPortal] Handler error:', e);
        }
      }
    }
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[PumpPortal] Max reconnect attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    console.log(`[PumpPortal] Reconnecting in ${delay}ms`);

    setTimeout(() => {
      this.connect().catch(console.error);
    }, delay);
  }
}

// Singleton instance
export const pumpPortal = new PumpPortalWebSocket();

/**
 * Normalize a PumpPortal token create event to our standard format
 */
export function normalizeNewToken(event: PumpTokenCreate) {
  const TARGET_SOL = 85;
  const INITIAL_VIRTUAL_SOL = 30;
  const currentSol = event.vSolInBondingCurve / 1e9;
  const progress = ((currentSol - INITIAL_VIRTUAL_SOL) / (TARGET_SOL - INITIAL_VIRTUAL_SOL)) * 100;

  return {
    address: event.mint,
    name: event.name,
    symbol: event.symbol,
    image: null, // Would need to fetch from URI
    platform: 'pump.fun' as const,
    creator: event.traderPublicKey,
    createdAt: Date.now(),
    graduated: false,
    marketCap: event.marketCapSol * 150, // Rough USD estimate (SOL ~$150)
    bondingProgress: Math.min(Math.max(progress, 0), 100),
    priceSol: event.vSolInBondingCurve / event.vTokensInBondingCurve,
    replies: 0,
    twitter: null,
    telegram: null,
    website: null,
    nsfw: false,
    // Default risk values for new tokens (will be analyzed async)
    riskScore: 50,
    riskLevel: 'medium' as const,
    holderCount: 1, // Just created, only creator holds it
  };
}

/**
 * Normalize a PumpPortal trade event
 */
export function normalizeTrade(event: PumpTrade) {
  return {
    signature: event.signature,
    tokenAddress: event.mint,
    type: event.txType,
    trader: event.traderPublicKey,
    solAmount: event.solAmount / 1e9,
    tokenAmount: event.tokenAmount,
    marketCapSol: event.marketCapSol,
    timestamp: Date.now(),
  };
}
